import { Box, Stack, Typography } from '@mui/material';

import type { RoadmapNode } from "../../api/roadmap-service";
import type { LessonStatuses } from "../../utils/roadmap-progress";
import { stationProgress } from "../../utils/roadmap-progress";
import { lineColor } from "../courses/metro/metro-theme";
import { RoadmapChoiceStation } from "./RoadmapChoiceStation";
import { RoadmapStation, stationState } from "./RoadmapStation";

const COLUMNS = 4;
const GAP = 40;

export type RoadmapLaneProps = {
    lane: RoadmapNode;
    index: number;
    level: number;
    statuses: LessonStatuses;
    levelName: (level: number) => string;
    coverOf: (courseId?: string) => string | undefined;
    onOpen: (node: RoadmapNode) => void;
};

/** One part of the role, e.g. Databases: its courses and choices as stations along a track. */
export function RoadmapLane({ lane, index, level, statuses, levelName, coverOf, onOpen }: RoadmapLaneProps) {
    const fromLevel = (node: RoadmapNode) => (node.level > level ? levelName(node.level) : undefined);

    return (
        <Stack direction={ { xs: 'column', md: 'row' } } spacing={ { xs: 1.5, md: 2.5 } } alignItems={ { md: 'center' } }
               sx={ { bgcolor: '#FFFFFF', border: '1px solid #E3E8EE', borderRadius: '20px', px: 2.5, py: 2.25, opacity: lane.level > level ? 0.6 : 1 } }>
            <Stack spacing={ 0.75 } sx={ { width: { md: 156 }, flexShrink: 0 } }>
                <Box aria-hidden="true" sx={ { width: 10, height: 10, borderRadius: '3px', bgcolor: lineColor(index) } }/>
                <Typography component="h3" variant="inherit" sx={ { fontSize: 16, lineHeight: '21px', fontWeight: 800 } }>{ lane.title }</Typography>
                <Typography sx={ { fontSize: 12, color: '#4F5B67' } }>{ laneMeta(lane, level, statuses) }</Typography>
            </Stack>

            <Box component="ol" sx={ {
                listStyle: 'none', m: 0, p: 0, flexGrow: 1, display: 'grid', gap: `16px ${ GAP }px`, alignItems: 'center',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: `repeat(${ COLUMNS }, minmax(0, 1fr))` },
            } }>
                { lane.children.map((station, position) => (
                    <Box component="li" key={ station.id ?? position } sx={ {
                        position: 'relative',
                        // The track between two stations of a row; rows and narrow screens read without it
                        [`&:not(:nth-of-type(${ COLUMNS }n+1))::before`]: {
                            content: "''", display: { xs: 'none', md: 'block' }, position: 'absolute', left: -GAP, width: GAP,
                            top: '50%', height: 3, mt: '-1.5px', bgcolor: '#DDE3EA',
                        },
                    } }>
                        { station.kind === 'CHOICE' ? (
                            <RoadmapChoiceStation node={ station } level={ level } statuses={ statuses } levelName={ levelName }
                                                  fromLevel={ fromLevel(station) } onOpen={ onOpen }/>
                        ) : (
                            <RoadmapStation node={ station } progress={ stationProgress(station, statuses) } cover={ coverOf(station.courseId) }
                                            fromLevel={ fromLevel(station) } onOpen={ () => onOpen(station) }/>
                        ) }
                    </Box>
                )) }
            </Box>
        </Stack>
    );
}

/** How far the user got with the lane's stations at the selected belt, e.g. "1 of 4 finished". */
function laneMeta(lane: RoadmapNode, level: number, statuses: LessonStatuses): string {
    if (lane.level > level) return 'Above your belt';

    const states = lane.children
        .filter((station) => station.level <= level && station.kind !== 'CHOICE')
        .map((station) => stationState(station, stationProgress(station, statuses)))
        .filter((state) => state !== 'planned' && state !== 'empty');

    if (!states.length) return 'Planned — not in the catalogue yet';

    const finished = states.filter((state) => state === 'finished').length;
    const started = states.filter((state) => state === 'progress').length;

    return finished ? `${ finished } of ${ states.length } finished` : `${ started } of ${ states.length } started`;
}
